import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { RadioButton } from 'primereact/radiobutton';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../../../contexts/LanguageContext';

const DEALER_TYPE = {
    SERVICE: 'SERVICE',
    SALES_AND_SERVICE: 'SALES_AND_SERVICE',
    SALES: 'SALES',
    SPARE_PARTS_ORDER: 'SPARE_PARTS_ORDER'
};
const NUM_TO_CODE = {
    1: DEALER_TYPE.SERVICE,
    2: DEALER_TYPE.SALES_AND_SERVICE,
    3: DEALER_TYPE.SALES,
    4: DEALER_TYPE.SPARE_PARTS_ORDER
};
const ALLOWED_CODES = new Set(Object.values(DEALER_TYPE));

const normalizeDealerType = (val) => {
    if (val == null) return null;
    if (typeof val === 'string') {
        const v = val.trim().toUpperCase();
        if (ALLOWED_CODES.has(v)) return v;
        const asNum = Number(v);
        if (!Number.isNaN(asNum) && NUM_TO_CODE[asNum]) return NUM_TO_CODE[asNum];
        return v;
    }
    if (typeof val === 'number') {
        return NUM_TO_CODE[val] || null;
    }
    return null;
};

const AuthSrcTypeDialog = ({ visible, data, onHide, onSave }) => {
    const { th } = useLanguage();
    const [submitted, setSubmitted] = useState(false);
    const [dateError, setDateError] = useState(false);

    const [formData, setFormData] = useState({
        id: null,
        startDate: null,
        endDate: null,
        dealerType: null,
        siteUseId: 0,
        termDateAutho: 1,
        isActive: true
    });

    const dealerTypeOptions = [
        { label: th('customerAddressBrand.tabs.authSrcType.dialog.options.service'), value: DEALER_TYPE.SERVICE },
        { label: th('customerAddressBrand.tabs.authSrcType.dialog.options.salesAndService'), value: DEALER_TYPE.SALES_AND_SERVICE },
        { label: th('customerAddressBrand.tabs.authSrcType.dialog.options.sales'), value: DEALER_TYPE.SALES },
        { label: th('customerAddressBrand.tabs.authSrcType.dialog.options.sparePartsOrder'), value: DEALER_TYPE.SPARE_PARTS_ORDER }
    ];

    useEffect(() => {
        setSubmitted(false);
        setDateError(false);

        if (data) {
            const start = data.startDate ? new Date(data.startDate) : null;
            let end = data.endDate ? new Date(data.endDate) : null;
            if (start && end && end < start) end = start;

            setFormData({
                id: data.id ?? null,
                startDate: start,
                endDate: end,
                dealerType: normalizeDealerType(data.dealerType),
                siteUseId: data.siteUseId || 0,
                termDateAutho: data.termDateAutho || 1,
                isActive: data.isActive === 1 || data.isActive === true
            });
        } else {
            setFormData({
                id: null,
                startDate: null,
                endDate: null,
                dealerType: null,
                siteUseId: 0,
                termDateAutho: 1,
                isActive: true
            });
        }
    }, [data, visible]);

    const handleChange = (field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'startDate' && updated.endDate && value && updated.endDate < value) {
                updated.endDate = value;
            }
            if (field === 'startDate' || field === 'endDate') setDateError(false);
            return updated;
        });
    };

    const handleSubmit = () => {
        setSubmitted(true);

        if (!formData.startDate || !formData.dealerType) return;

        if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
            setDateError(true);
            return;
        }

        onSave({ ...formData, dealerType: normalizeDealerType(formData.dealerType) });
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    return (
        <Dialog
            header={data ? th('customerAddressBrand.tabs.authSrcType.dialog.title.update') : th('customerAddressBrand.tabs.authSrcType.dialog.title.new')}
            visible={visible}
            style={{ width: '45vw' }}
            modal
            onHide={onHide}
            className="p-fluid"
            footer={footer}
        >
            <div className="formgrid grid">
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.authSrcType.dialog.label.startDate')}</label>
                    <Calendar
                        value={formData.startDate}
                        onChange={(e) => handleChange('startDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.authSrcType.dialog.placeholder.startDate')}
                        className={submitted && !formData.startDate ? 'p-invalid' : ''}
                        inputClassName="w-full"
                    />
                    {submitted && !formData.startDate && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.authSrcType.dialog.label.endDate')}</label>
                    <Calendar
                        value={formData.endDate}
                        onChange={(e) => handleChange('endDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.authSrcType.dialog.placeholder.endDate')}
                        minDate={formData.startDate || null}
                        className={dateError ? 'p-invalid' : ''}
                        inputClassName="w-full"
                    />
                    {dateError && <small className="p-error">{th('customerAddressBrand.tabs.authSrcType.dialog.error.endDateBeforeStartDate')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.authSrcType.dialog.label.dealerType')}</label>
                    <Dropdown
                        value={formData.dealerType}
                        options={dealerTypeOptions}
                        onChange={(e) => handleChange('dealerType', e.value)}
                        placeholder={th('customerAddressBrand.tabs.authSrcType.dialog.placeholder.dealerType')}
                        className={submitted && !formData.dealerType ? 'p-invalid' : ''}
                    />
                    {submitted && !formData.dealerType && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.authSrcType.dialog.label.termDateAutho')}</label>
                    <div className="flex align-items-center gap-4 mt-1">
                        <div className="flex align-items-center">
                            <RadioButton inputId="termYes" name="termDateAutho" value={1} onChange={(e) => handleChange('termDateAutho', e.value)} checked={formData.termDateAutho === 1} />
                            <label htmlFor="termYes" className="ml-2">
                                {th('customerAddressBrand.tabs.authSrcType.dialog.radio.yes')}
                            </label>
                        </div>
                        <div className="flex align-items-center">
                            <RadioButton inputId="termNo" name="termDateAutho" value={2} onChange={(e) => handleChange('termDateAutho', e.value)} checked={formData.termDateAutho === 2} />
                            <label htmlFor="termNo" className="ml-2">
                                {th('customerAddressBrand.tabs.authSrcType.dialog.radio.no')}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AuthSrcTypeDialog;
